export default {
  en: {
    title: 'Settings',
    menu: {
      userSettings: 'User settings',
      account: 'Account & password',
      appearance: 'Appearance',
      notifications: 'Notifications',
      orgSettings: 'Organisation settings',
      organisation: 'Organisation',
      approvalConfiguration: 'Supplier approval',
      manageUsers: 'Users',
      manageUsersAndRoles: 'Users & roles',
      supplierLabels: 'Supplier labels',
      subEntityLabels: 'Sub-entity labels',
      customProperties: 'Custom properties',
      sso: 'Single sign-on',
      visualisation: 'Visualisation',
      networkAndCommunity: 'Network & community',
      api: 'API keys',
      discussions: 'Discussions',
      risks: 'Risks',
      framework: 'Framework & add-ons',
      products: 'Products and services',
    },
    labels: {
      email: 'Email',
      name: 'Name',
      firstName: 'First name',
      lastName: 'Last name',
      team: 'Team',
      jobTitle: 'Job title',
      linkedinUrl: 'LinkedIn URL',
      phone: 'Mobile number',
      password: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      role: 'Role',
      admin: 'Admin',
      edit: 'Edit',
      view: 'View',
      modePermission: 'Mode permission',
      client: 'Client',
      supplier: 'Supplier',
      client_supplier: 'Client & supplier',
      federated: 'Federated',
      supplierVisibility: 'Supplier visibility',
    },
    placeholder: {
      team: 'Select your team...',
      userTeam: 'Select team...',
    },
    buttons: {
      save: 'Save',
      changePassword: 'Change password',
      useSMS: 'Use SMS',
      edit: 'Edit',
      delete: 'Delete',
      cancel: 'Cancel',
      confirm: 'Confirm',
      addUser: 'Add user',
      resend: 'Resend password',
    },
    restrictDropdown: {
      header: 'Which suppliers should this user see?',
      allSuppliers: 'All suppliers',
      allSuppliersDescription: "Every supplier you're connection with.",
      assignedSuppliers: 'Assigned suppliers',
      assignedSuppliersDescription: "Only suppliers they've been assigned to.",
    },
    error: {
      updateDetails: 'Failed updating account details',
      delete: 'Failed deleting user',
      userCreated: 'Error creating user',
      userAlreadyExists: 'Email already in use',
      resend: 'Failed sending new temporary password',
    },
    message: {
      authenticatorStep:
        'Scan the QR code using your authenticator app, or copy in the secret key.',
      authenticatorStepCode: 'Enter the generated code below.',
      secretKeyTooltip:
        "If you're unable to scan the above QR code, enter this secret code into your app manually.",
      detailsUpdated: 'Details updated',
      delete: 'User has been deleted',
      userCreated: 'User has been created',
      resend: 'Temporary password has been resent',
      passwordRules:
        'Password must be at least 8 characters and contain at least 1 uppercase character, 1 number and 1 symbol',
      passwordConfirm: 'Passwords do not match',
      passwordChanged: 'Password changed',
    },
    account: {
      details: 'Details',
      password: 'Change password',
      secretKey: 'Secret key',
      mfa: 'Configure 2FA',
      ownDetailsUpdated: 'Your details have been updated',
    },
    manageUsers: {
      users: 'Manage users',
      title: 'Manage users & roles',
      descTitle: 'Each of your users has a role that determines their permissions on Risk Ledger.',
      desc: "<b>View</b>: can only view the data available to your organisation.<br><b>Edit</b>: like view, but can also do most activities such as editing assessment, posting comments, managing policies, and more.<br><b>Admin</b>: like edit, but can also manage users and organisation-wide settings.<br><b>Lead</b>: your organisation's first user, with same permissions as Admin.",
      deleteTitle: "Delete user '{name}'?",
      deleteDescription: 'Are you sure you want to delete this user? This action is permanent.',
      reasonPlaceholder: 'Why are you deleting this user?',
      addNewUser: {
        title: 'Add new user',
        primerEmail: {
          desc: 'I confirm a {primerEmail} has been sent to this user.',
          link: 'Primer email',
        },
      },
      addRole: 'Create role',
      columns: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
        visibility: 'Supplier visibility',
        actions: 'Actions',
      },
      userUnregistered: "User hasn't signed up.",
      resendPassword: 'Resend password',
      exportCSV: 'Export CSV',
      exportUser: 'User',
      exportRole: 'Role',
      exportUserHeaders: {
        name: 'Name',
        email: 'Email',
        role: 'Role',
        supplierVisibility: 'Supplier visibility',
        jobTitle: 'Job title',
        team: 'Team',
        createdAt: 'Created at',
        deletedAt: 'Deleted at',
        deleted: 'Deleted',
      },
      exportUserValues: {
        assignedUsers: 'Assigned users',
        allSuppliers: 'All suppliers',
      },
      exportRoleHeaders: {
        roleName: 'Role name',
        assignedUsers: 'Number of assigned users',
        createdAt: 'Created at',
      },
    },
    manageRoles: {
      usersWithRole: 'Not used | 1 user | {count} users',
      edit: 'Edit',
      columns: {
        name: 'Role',
        assignedUsers: 'Assigned users',
        actions: 'Actions',
      },
      disabledReasons: {
        default: 'This role is a managed role',
        sso: 'This role is used for single sign-on provisioning',
      },
      defaultProvisioning: 'Default provisioning role. You can update that',
      here: 'here',
    },
    addRole: {
      intro: 'Choose an existing role to copy or start one from scratch.',
      step: 'Step {step}/2',
      roles: {
        blankTitle: 'Blank',
        blankDescription: 'Start from scratch with no permissions preselected.',
        viewDescription: 'Basic permissions have already been set but can be customised further.',
        editDescription:
          "More advanced permissions have been set including everything from 'View'.",
        customDescription: "All permissions you've previously set are included.",
      },
      create: 'Create role',
      roleName: 'Role name',
      roleNamePlaceholder: 'Set role name',
      federatedPermissions: 'Federated permissions',
      clientPermissions: 'Client permissions',
      clientPermissionsSubtitle:
        'Granting permissions here allows users access to the client side of the platform.',
      supplierPermissions: 'Supplier permissions',
      supplierPermissionsSubtitle:
        'Granting permissions here allows users access to the supplier side of the platform.',
      settingsPermissions: 'Settings permissions',
      nameTaken: 'Please choose a unique name for this role',
      successCta: 'Custom role created!',
      errorNameTaken: 'Failed to create role - name already taken',
      errorMisc: 'Failed to create role for an unknown reason',
      learnMore: 'Learn more',
      searchPlaceholder: 'Filter permissions...',
    },
    editRole: {
      title: 'Edit role',
      successCta: 'Custom role updated!',
      errorNameTaken: 'Failed to edit role - name already taken',
      errorMisc: 'Failed to edit role for an unknown reason',
      save: 'Save',
    },
    org: {
      info: 'Basic information',
      legalName: 'Legal name',
      companyNumber: 'Company registration number',
      tradingNames: 'Trading names',
      tradingNamesTooltip: 'Press enter to submit each entry.',
      website: 'Website',
      businessPhoneNumber: 'Business phone number',
      sector: 'Sector',
      office: 'Registered office',
      streetAddress: 'Street address',
      city: 'City',
      postCode: 'Postcode/ZIP code',
      country: 'Country',
      save: 'Save changes',
      saveFailed: 'Saving failed. Please try again.',
      logo: 'Company logo',
      edit: 'Edit',
    },
    help: {
      teams: 'Please select the team that best describes your role.',
      modePermission: 'Please select the platform side you would like to give the user access to.',
      client: 'The user only has access to the client side of the platform.',
      supplier: 'The user only has access to the supplier side of the platform.',
      client_supplier: 'The user has access to both client and supplier sides of the platform.',
      role: 'Your role on Risk Ledger, only Admin users are able edit this.',
    },
    networkAndCommunity: {
      networkVisualisation: {
        title: 'Enable network visualisation',
        description1:
          'By enabling visualisation, you will be able to see the names of all organisations within your supply chain, including 4th, 5th and 6th parties.',
        description2: 'Your clients will also be able to see the names of your suppliers.',
        description3:
          'Your supply chain will appear to other organisations on the network and any communities of which you are a member.',
      },
      excludeSuppliers: {
        title: 'Exclude suppliers',
        description:
          'Select which suppliers you would like to exclude from the visualisation, the names of these suppliers will not be shared with your clients.',
        placeholder: 'Search...',
      },
      privateConnections: {
        title: 'Hide suppliers',
        description:
          'Hide individual suppliers if they are confidential or subject to non-disclosure agreements. This will prevent them from appearing as connected to your organisation in the network map or supplier lists.',
        supplierName: 'Supplier name',
        toggledPrivateBy: 'Added by',
        addedBy: 'Added by',
        addButton: 'Hide supplier',
        privateConnection: 'Private supplier',
        markAsPrivateButton: 'Mark as private',
        searchPlaceholder: 'Search from your suppliers...',
        closeButton: 'Done',
      },
      fetchError: 'Failed to get network configuration.',
      updateError: 'Failed to update network configuration.',
    },
    frameworkDefaultConfig: {
      title: 'Framework & add-on defaults',
      headline:
        "Select the default controls your organisation wants your suppliers to complete. These will be set as recommendations when you connect with a supplier, and you can override these defaults on each supplier's profile.",
      levelsTitle: 'Framework size',
      levelsDescription:
        'Select the default framework size your organisation wants your suppliers to complete.',
      addOnsTitle: 'Add-ons (optional)',
      addOnsDescription:
        'Select any additional default domains you want your suppliers to complete.',
      success: 'Default framework settings saved',
      error: 'Failed to save default framework settings',
    },
    appearance: {
      themeTitle: 'Theme',
      themeText:
        "Choose how you'd like Risk Ledge to appear. Select a theme or sync themes with your system preferences.",
      themeTitles: {
        classic: 'Classic',
        system: 'Sync with system',
        light: 'Light mode',
        dark: 'Dark mode',
      },
      themeHelpText: {
        classic: 'Mixed light and dark backgrounds',
        system: 'Matches with your system settings',
        light: 'Dark text on light backgrounds',
        dark: 'Light text on dark backgrounds',
      },
    },
  },
};
